<template>
<div class="flex flex-1 flex-col items-start self-stretch rounded-[16px] border border-[#EAEAEA] bg-white p-6 max-md:mx-[21px] gap-8">
    <div v-if="showBestMatch && bestMatchDetails">
            <BestMatchProductCard :bestMatchDetails="bestMatchDetails" :bestMatchDetailsContent="bestMatchDetailsContent" :analyticsContext="bestMatchAnalyticsContext"/>
    </div>
    <div class="grid w-full grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-6">
            <LazyProductCard v-for="(item,index) in productList" :key="`${item.id}-${index}`" v-bind="item" :indexValue="index" />
    </div>
</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import BestMatchProductCard from '~/components/ui/organism/product/agentic/bestMatchProductCard.vue'
import { convertPriceStringToFloat } from '~~/shared/utils/price'

import type {
    AddToCartAnalyticsContext,
    AgenticProduct,
    FilterQueryResponse
} from '~~/shared/types/common'

const props = defineProps < {
    productData: FilterQueryResponse
} > ()

const LazyProductCard = defineLazyHydrationComponent(
    'visible',
    () => import('~/components/ui/organism/product/productTile.vue')
)

const rawProducts = computed(() => {
    return props.productData.products ?? []
})

const bestMatchId = computed(() => {
    return props.productData.best_match_product ?.product ?.id
})

const bestMatchDetails = computed<AgenticProduct | null>(() => {
    return props.productData.best_match_product?.product ?? null
})

const bestMatchDetailsContent = computed(() => {
    return props.productData.best_match_product?.content ?? undefined
})

const showBestMatch = computed(() => {
    return props.productData.product_count < 10 && !!bestMatchDetails.value
})

const buildAnalyticsContext = (product: AgenticProduct | null | undefined): AddToCartAnalyticsContext => {
    const price = convertPriceStringToFloat(product?.price)

    return {
        screen_name: 'Agenticplp_results',
        sub_screen: 'Product_summary',
        item_id: product?.sku,
        item_name: product?.productName ?? product?.name,
        item_category: product?.searchTag ?? product?.benefits?.[0],
        price: Number.isFinite(price) ? price : undefined,
        item_list_id: 'agentic_plp_results',
        item_list_name: 'Agentic PLP Results'
    }
}

const bestMatchAnalyticsContext = computed<AddToCartAnalyticsContext>(() => {
    return buildAnalyticsContext(bestMatchDetails.value)
})

const toStringArray = (value: unknown[] | undefined): string[] => {
    if (!Array.isArray(value)) {
        return []
    }

    const list: unknown[] = value
    return list.filter((item): item is string => typeof item === 'string' && value.length > 0)
}

const displayProducts = computed(() => {
    return rawProducts.value.filter((product) => {
        return product.id !== bestMatchId.value
    })
})

const formatBenefits = (benefits: unknown[] = []) => {
    const normalizedBenefits = toStringArray(benefits)
    if (normalizedBenefits.length <= 2) {
        return normalizedBenefits.join(', ')
    }

    return `${normalizedBenefits.slice(0, 2).join(', ')} +${normalizedBenefits.length - 2} more`
}

const resolveProductBadge = (badges: unknown[] = [], customBadges: unknown[] = []) => {
    const normalizedCustomBadges = toStringArray(customBadges)
    if (normalizedCustomBadges.length) {
        return normalizedCustomBadges[0]
    }

    return toStringArray(badges)[0]
}

const productList = computed(() => {
    return displayProducts.value.map((product) => ({
        id: product.id,
        sku: product.sku ?? '',
        name: product.productName,
        url: product.url,
        primaryImageUrl: product.imageUrl,
        benefits: formatBenefits((product.benefits ?? []) as unknown[]),
        headline: product.headline,
        shortDescription: product.shortDescription,
        reviewSummary: {
            averageRating: product.averageRating ?? 0,
            reviewCount: product.reviewCount ?? 0
        },
        tag: resolveProductBadge(
            toStringArray(product.badges),
            toStringArray(product.customBadges)
        ),
        pricing: {
            price: product.price,
            retailPrice: product.retailPrice,
            wholesalePrice: product.wholesalePrice,
            hasMultiplePrices: (product as AgenticProduct & { hasMultiplePrices?: boolean | null }).hasMultiplePrices,
            loyaltyPointCost: product.loyaltyPointCost
        },
        analyticsContext: buildAnalyticsContext(product)
    }))
})
</script>
